import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import JsonLd, { SITE_URL } from "@/components/feature/JsonLd";

const MAIN_SITE = "https://kaspreadsheet.com";

const FAQ_LIST = [
  {
    q: "What is the USFans Spreadsheet?",
    a: "It's a resource that organizes product and seller information into an easier format for research, comparison and discovery. Instead of jumping between many pages, you can scan everything in one place.",
  },
  {
    q: "How do I use the USFans Spreadsheet?",
    a: "Use it to browse products by category, compare sellers, check shipping details and keep your research organized before you make a purchase. Start with the category that interests you most.",
  },
  {
    q: "Is the USFans Spreadsheet free?",
    a: "Spreadsheet resources are generally informational. Always check the main website for the latest details, availability and any associated costs. Some features may require registration.",
  },
  {
    q: "Is USFans safe to use?",
    a: "Always verify sellers, links and payment details independently. Our safety resources explain the key checks to make before any decision. No resource can replace your own careful judgment.",
  },
  {
    q: "How much is USFans shipping?",
    a: "Shipping costs and delivery times can vary by location and seller. Review the shipping details for each item and check the main website for current information. International shipping is usually available.",
  },
  {
    q: "Can I trust every seller in the spreadsheet?",
    a: "A spreadsheet organizes information but does not guarantee seller quality. Always research each seller independently, check reviews and start with smaller transactions when trying a new seller.",
  },
  {
    q: "What categories are available on USFans?",
    a: "USFans covers a wide range of fashion and lifestyle products including Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jerseys, Accessories, Bags, Belts, Perfumes and more.",
  },
  {
    q: "How often is the spreadsheet updated?",
    a: "Update frequency depends on the source. Independent resource hubs like USFansLink aim to keep information current, but always cross-reference with the official website for the most up-to-date details.",
  },
  {
    q: "Are there any discount codes or coupons?",
    a: "Coupons and discount codes may be available from time to time. Check our coupon guide or the main website for any active promotions before you complete your purchase.",
  },
  {
    q: "What should I do if something goes wrong with an order?",
    a: "Document everything — screenshots, messages and payment confirmations. Contact the seller first. If unresolved, reach out to the payment provider for buyer protection options.",
  },
  {
    q: "Can I use the USFans Spreadsheet on mobile?",
    a: "Yes. The main website and most spreadsheet formats work well on mobile browsers. For the best experience, use a modern browser and a stable internet connection.",
  },
  {
    q: "Where can I learn more about USFans warehouse and shipping?",
    a: "Visit our shipping guide for detailed information about warehouse locations, processing times, shipping methods and international delivery options.",
  },
];

export default function FaqPage() {
  const { lang } = useParams();
  const [open, setOpen] = useState<number | null>(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <article className="w-full">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "en",
          mainEntity: FAQ_LIST.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      {/* Hero */}
      <div className="relative bg-background-100 border-b border-background-200">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
          <nav className="text-xs text-foreground-500 mb-5 text-left">
            <Link to={`/${lang}`} className="hover:text-foreground-900 transition">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">FAQ</span>
          </nav>

          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 mx-auto mb-5">
            <i className="ri-question-answer-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Quick answers to the most common questions about the USFans Spreadsheet, safety, shipping, categories and how to get started.
          </p>
        </div>
      </div>

      {/* FAQ list */}
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="space-y-3">
          {FAQ_LIST.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                className="rounded-lg border border-background-200 bg-background-50 overflow-hidden"
              >
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-heading text-sm md:text-base font-semibold text-foreground-950">
                    {f.q}
                  </span>
                  <span
                    className={`w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 shrink-0 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  >
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5">
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      {f.a}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Still have questions */}
        <div className="mt-12 rounded-lg bg-secondary-50 border border-secondary-200 p-6 md:p-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
            Still Have Questions?
          </h2>
          <p className="text-foreground-600 mb-5 leading-relaxed">
            Explore more detailed guides on our website or visit the main USFans Spreadsheet for the latest information and support.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link
              to={`/${lang}/article/usfans-spreadsheet-guide`}
              className="h-11 px-6 inline-flex items-center rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition whitespace-nowrap"
            >
              Read the Full Guide
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <a
              href={MAIN_SITE}
              target="_blank"
              rel="noopener nofollow"
              className="h-11 px-6 inline-flex items-center rounded-md border border-background-300 text-foreground-900 font-semibold hover:bg-background-200 transition whitespace-nowrap"
            >
              Visit Main Website
              <i className="ri-arrow-right-up-line ml-2"></i>
            </a>
          </div>
        </div>

        {/* Related links */}
        <div className="mt-10">
          <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
            Related Resources
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to={`/${lang}/safety`}
              className="flex items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition"
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-900 shrink-0">
                <i className="ri-shield-check-line text-xl"></i>
              </span>
              <div>
                <p className="font-semibold text-foreground-950 text-sm">Safety Center</p>
                <p className="text-xs text-foreground-500">Tips to stay safe while researching</p>
              </div>
            </Link>
            <Link
              to={`/${lang}/article/usfans-shipping-cost`}
              className="flex items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition"
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-900 shrink-0">
                <i className="ri-truck-line text-xl"></i>
              </span>
              <div>
                <p className="font-semibold text-foreground-950 text-sm">Shipping Guide</p>
                <p className="text-xs text-foreground-500">Costs, methods and delivery info</p>
              </div>
            </Link>
            <Link
              to={`/${lang}/article/usfans-coupon-discount`}
              className="flex items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition"
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-900 shrink-0">
                <i className="ri-coupon-line text-xl"></i>
              </span>
              <div>
                <p className="font-semibold text-foreground-950 text-sm">Coupon Guide</p>
                <p className="text-xs text-foreground-500">Find discounts and promo codes</p>
              </div>
            </Link>
            <Link
              to={`/${lang}`}
              className="flex items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition"
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 shrink-0">
                <i className="ri-home-4-line text-xl"></i>
              </span>
              <div>
                <p className="font-semibold text-foreground-950 text-sm">Home</p>
                <p className="text-xs text-foreground-500">Browse all categories and guides</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}